import * as S from "effect-app/Schema"
import { type featureFlagDefinitions } from "./env.ts"
import { type FeatureFlagDefinition } from "./featureFlag.ts"

export type FeatureFlagDefinitions = ReturnType<typeof featureFlagDefinitions>
export type KnownFeatureFlagName = Extract<keyof FeatureFlagDefinitions, string>
export type FeatureFlagName = string
export const FeatureFlagName = S.String

type FeatureFlagDefinitionMap = Readonly<Record<string, FeatureFlagDefinition>>
type FeatureFlagsFromDefinitions<T extends FeatureFlagDefinitionMap> = {
  readonly [K in keyof T]: boolean
}
type MutableFeatureFlagsFromDefinitions<T extends FeatureFlagDefinitionMap> = {
  -readonly [K in keyof T]: boolean
}

export type FeatureFlags = FeatureFlagsFromDefinitions<FeatureFlagDefinitions> & Readonly<Record<string, boolean>>

export const FeatureFlagsSchema = S.Record(S.String, S.Boolean)

export interface FeatureFlagOverrideLike {
  value: boolean
  activeFrom: Date | null
}

const definitionEntries = <T extends FeatureFlagDefinitionMap>(definitions: T) =>
  Object.entries(definitions) as Array<[Extract<keyof T, string>, FeatureFlagDefinition]>

export const featureFlagDefaultValue = (definition: FeatureFlagDefinition, now: Date) =>
  definition.activeFrom === null || definition.activeFrom.getTime() <= now.getTime()
    ? definition.value
    : !definition.value

export const featureFlagsFromDefinitions = <T extends FeatureFlagDefinitionMap>(
  definitions: T,
  now: Date
): FeatureFlagsFromDefinitions<T> => {
  const flags = {} as MutableFeatureFlagsFromDefinitions<T>
  for (const [name, definition] of definitionEntries(definitions)) {
    flags[name] = featureFlagDefaultValue(definition, now)
  }
  return flags
}

const isFeatureFlagOverrideActive = (override: FeatureFlagOverrideLike, now: Date) =>
  override.activeFrom === null || override.activeFrom.getTime() <= now.getTime()

export const resolveFeatureFlags = <T extends FeatureFlagDefinitionMap>(
  definitions: T,
  overrides: ReadonlyMap<string, FeatureFlagOverrideLike>,
  now: Date
): FeatureFlagsFromDefinitions<T> & Readonly<Record<string, boolean>> => {
  const resolved: Record<string, boolean> = { ...featureFlagsFromDefinitions(definitions, now) }
  for (const [name, override] of overrides) {
    if (isFeatureFlagOverrideActive(override, now)) {
      resolved[name] = override.value
    }
  }
  return resolved as FeatureFlagsFromDefinitions<T> & Readonly<Record<string, boolean>>
}

export const featureFlagStates = <T extends FeatureFlagDefinitionMap>(
  definitions: T,
  overrides: ReadonlyMap<string, FeatureFlagOverrideLike>,
  now: Date
) => {
  const knownNames = new Set(Object.keys(definitions))
  const unknownOverrideNames = [...overrides.keys()]
    .filter((name) => !knownNames.has(name))
    .sort()

  return [
    ...definitionEntries(definitions).map(([name, definition]) => {
      const override = overrides.get(name) ?? null
      const active = override !== null && isFeatureFlagOverrideActive(override, now)
      const defaultActive = definition.activeFrom === null || definition.activeFrom.getTime() <= now.getTime()
      const currentDefaultValue = featureFlagDefaultValue(definition, now)
      return {
        name,
        description: definition.description,
        defaultValue: definition.value,
        defaultActiveFrom: definition.activeFrom,
        defaultActive,
        effectiveValue: active ? override.value : currentDefaultValue,
        overrideValue: override?.value ?? null,
        activeFrom: override?.activeFrom ?? null,
        active
      }
    }),
    ...unknownOverrideNames.map((name) => {
      const override = overrides.get(name) ?? null
      const active = override !== null && isFeatureFlagOverrideActive(override, now)
      return {
        name,
        description: "Persisted override without a code default.",
        defaultValue: false,
        defaultActiveFrom: null,
        defaultActive: true,
        effectiveValue: active ? override.value : false,
        overrideValue: override?.value ?? null,
        activeFrom: override?.activeFrom ?? null,
        active
      }
    })
  ]
}
